
import { DateTime } from 'luxon';
import { LogRecordState, LogRecordType, Log_EntryBase, assureIdForEntry } from './user-log';
import { BFData } from './user-state';
import { FatTrendDirection, ProgressStatus } from '../body-comp-overview';
import { BodyFatSeries } from '../plan-update-internals';


export enum CalorieIntakeBaseline {
  Logged = 'logged',
  Target = 'target',
  Custom = 'custom',
}

export enum WeeklyCheckupCompletionAction {
  Accepted = 'accepted',
  Declined = 'declined',
  Finished = 'finished', // No adjustments were needed
  AwaitingCoach = 'awaitingCoach', // coached client submitted for coach review
}

export enum CoachResolvedAction {
  Accepted = 'accepted',
  Modified = 'modified',
  Declined = 'declined',
}

export interface Log_WeeklyCheckup extends Log_EntryBase {
  type: LogRecordType.WeeklyCheckup;

  selectedCalorieIntakeBaseline: CalorieIntakeBaseline;
  calorieIntakeBaseline: number;
  recommendedAvgCalories: number;
  isWeightGainGoal: boolean;
  bfData?: BFData;
  fitnessPlanBackupId?: string;
  completionAction?: WeeklyCheckupCompletionAction;
  /** true when progress called for eating less but the daily target was already at the
   *  minimum daily intake, so nothing was changed (#5415). absent on older checkups. */
  atMinimumIntake?: boolean;
  /** true when the recommended change was 1% of the target or less for any other reason, so
   *  nothing was offered or changed (#3528). absent on older checkups. */
  changeTooSmall?: boolean;

  // meal-level calories (cutMealCalories/bulkMealCalories set when cycling enabled, avgMealCalories when disabled)
  avgMealCalories?: number;
  cutMealCalories?: number;
  bulkMealCalories?: number;

  // populated while completionAction === AwaitingCoach; JSON-serialized to
  // keep the CouchDB doc decoupled from the plan/fitnessState schema
  pendingNewPlan?: string;
  pendingNewFitnessState?: string;

  // populated once the coach resolves an AwaitingCoach checkup
  coachResolvedAction?: CoachResolvedAction;
  coachModifiedCalories?: number;
  coachResolvedAtUtc?: string;

  /**
   * The daily target a person without a coach set for themselves with Modify in the check-up,
   * and accepted (#5456). ABSENT on older check-ups and whenever the app's own number was taken.
   * Read it through `readPositiveCalories`.
   */
  userModifiedCalories?: number;
  /**
   * The daily target the app recommended, kept when a person or their coach replaced it with
   * their own (#5456). ABSENT on older check-ups and when nothing was replaced.
   * Read it through `readPositiveCalories`.
   */
  appRecommendedAvgCalories?: number;
  /**
   * The lowest daily target the solver would have set for this person when the check-up's solve
   * ran (#5456). Not a recommendation, and no client screen shows it; the Modify control warns
   * when a typed target is below it. ABSENT on older check-ups and when the calorie step did not
   * run. Read it through `readPositiveCalories`.
   */
  minimumDailyIntake?: number;

  /**
   * The local ISO dates (yyyy-mm-dd, in each day entry's own time zone) of the days the user
   * left ticked as fully logged on the food step (#5456). Written whenever that step was shown.
   * ABSENT on check-ups made before this field existed and on check-ups that skipped the food
   * step. Read it only through `readLoggedDayDates`, which tolerates a damaged value.
   */
  loggedDayDates?: string[];

  /**
   * What the progress check said when this check-up was completed (#5456). The check can only
   * judge "now", so a completed check-up cannot work its verdict out again later; this is the
   * record. Holds no calorie recommendation. ABSENT on check-ups made before this field existed.
   * Read it only through `readProgressSnapshot`, which tolerates a damaged value.
   */
  progressSnapshot?: CheckupProgressSnapshot;

  /**
   * How many exercises of the program had a newest session marked "the same as last time" on the
   * Training Overview when this check-up was completed (#5456, phase 3). A completed check-up
   * shows the count as it was that day. They count the person's own logged sets, so they hold
   * nothing a coached client may not see. ABSENT on older check-ups and when the overview had not
   * loaded by then. Read both through `readTrainingCounts`, which tolerates a damaged value.
   */
  trainingSameCount?: number;
  /** the same, for a newest session marked "down from last time"; ABSENT under the same conditions */
  trainingDownCount?: number;
}

/** the two Training Overview counts stored on a completed check-up */
export interface CheckupTrainingCounts {
  same: number;
  down: number;
}

/**
 * The Training Overview counts stored on a check-up, or `undefined` when it stores none. Both
 * must be non-negative whole numbers: anything else (an older check-up, a damaged value) reads as
 * absent, and the check-up then shows its Training Overview button with no count.
 */
export function readTrainingCounts(
  checkup: Pick<Log_WeeklyCheckup, 'trainingSameCount' | 'trainingDownCount'> | null | undefined,
): CheckupTrainingCounts | undefined {
  const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;
  const same: unknown = checkup?.trainingSameCount;
  const down: unknown = checkup?.trainingDownCount;
  return isCount(same) && isCount(down) ? { same, down } : undefined;
}

/** The verdict and trend figures stored on a completed check-up. Every field can be missing. */
export interface CheckupProgressSnapshot {
  /** the status under the check-up's own rule (`ProgressStatusRule.Checkup`) */
  status?: ProgressStatus;
  fatTrend?: FatTrendDirection;
  /** which body-fat series `fatTrend` and the two fat slopes were read from */
  fatnessSeries?: BodyFatSeries;
  weightSlope_kgwk?: number;
  estimatedBW_kg?: number;
  /** the 30th and 70th percentile slopes of the fat series, per day */
  fatnessSlopeBottom?: number;
  fatnessSlopeTop?: number;
  /** the calendar day of the earliest reading the check fitted */
  firstCountedDayIso?: string;
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The ticked days stored on a check-up, or `undefined` when it stores none.
 *
 * A document can come from an older version of the app or be damaged, so the shape is not
 * trusted: a value that is not an array reads as absent, and entries that are not ISO dates are
 * dropped. An empty array is a real answer ("the food step was shown and no day was ticked").
 */
export function readLoggedDayDates(checkup: Pick<Log_WeeklyCheckup, 'loggedDayDates'> | null | undefined): string[] | undefined {
  const stored: unknown = checkup?.loggedDayDates;
  if (!Array.isArray(stored)) {
    return undefined;
  }
  return stored.filter((day): day is string => typeof day === 'string' && ISO_DATE_PATTERN.test(day));
}

/**
 * The verdict stored on a check-up, or `undefined` when it stores none.
 *
 * Normalised the same way as `readLoggedDayDates`: a value that is not an object reads as
 * absent, an enum value this version does not know is dropped, and so is a number that is not
 * finite. An object with nothing usable left in it reads as absent too.
 */
export function readProgressSnapshot(checkup: Pick<Log_WeeklyCheckup, 'progressSnapshot'> | null | undefined): CheckupProgressSnapshot | undefined {
  const stored: unknown = checkup?.progressSnapshot;
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return undefined;
  }
  const raw = stored as { [key: string]: unknown };
  const snapshot: CheckupProgressSnapshot = {};
  const enumValue = <T extends string>(value: unknown, known: { [key: string]: T }): T | undefined =>
    Object.values(known).some(k => k === value) ? value as T : undefined;
  const finite = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined;

  snapshot.status = enumValue(raw.status, ProgressStatus);
  snapshot.fatTrend = enumValue(raw.fatTrend, FatTrendDirection);
  snapshot.fatnessSeries = enumValue(raw.fatnessSeries, BodyFatSeries);
  snapshot.weightSlope_kgwk = finite(raw.weightSlope_kgwk);
  snapshot.estimatedBW_kg = finite(raw.estimatedBW_kg);
  snapshot.fatnessSlopeBottom = finite(raw.fatnessSlopeBottom);
  snapshot.fatnessSlopeTop = finite(raw.fatnessSlopeTop);
  snapshot.firstCountedDayIso = typeof raw.firstCountedDayIso === 'string' && ISO_DATE_PATTERN.test(raw.firstCountedDayIso)
    ? raw.firstCountedDayIso
    : undefined;

  // drop the keys left undefined, so the object can be stored and compared as it is
  for (const key of Object.keys(snapshot) as (keyof CheckupProgressSnapshot)[]) {
    if (snapshot[key] === undefined) delete snapshot[key];
  }
  return Object.keys(snapshot).length > 0 ? snapshot : undefined;
}

/**
 * A stored calorie figure, or `undefined` when the document holds none. Anything that is not a
 * positive finite number reads as absent, so a document from an older version or a damaged one
 * never puts a wrong number on screen. For `userModifiedCalories`, `appRecommendedAvgCalories`
 * and `minimumDailyIntake`.
 */
export function readPositiveCalories(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

export function createWeeklyCheckupEntry(date_local: DateTime): Log_WeeklyCheckup {
  let entry = {} as Log_WeeklyCheckup;
  entry.time_utc = date_local.toUTC().toISO();
  entry.timeOffset_min = date_local.offset;
  entry.type = LogRecordType.WeeklyCheckup;
  entry.state = LogRecordState.Scheduled;

  assureIdForEntry(entry);
  return entry;
}